import { useMemo, useCallback } from 'react';
import { useQuery, useApolloClient } from '@apollo/client';

import { buildDateRange } from 'utils/Appointments';
import userInfoQuery from 'utils/User/currentUserProfile.graphql';

import assetAlertsQuery from './assetAlerts.graphql';

export default function useAlertsQuery(
  {
    alertTypes,
    alertsText,
    scheduleMaintenanceStatus,
    faultStatus,
    customAlertStatus,
    sort,
    sortDirection,
    groupBy,
    assetTypes,
    makes,
    models,
    fleetInfo,
    years,
    costCenters,
    workInProgress,
    customAlerts,
    customerIds,
    platformAccountIds,
    groupedAssetGroupingsIds,
    enabled,
    currentPage,
    assignees,
    platformCaseFleetStatus,
    platformCaseRepairStatus,
    serviceEventStatus,
    serviceRequestStatus,
    text,
    serviceEventCreatedBetween,
    serviceEventUpdatedBetween,
    serviceRequestCreatedBetween,
    serviceRequestUpdatedBetween,
    platformCaseCreatedBetween,
    platformCaseUpdatedBetween,
    serviceProviderUuids,
    serviceProviderCityIds,
    includeClosedPlatformCases,
    assetsQueryString,
    updateAssetsQueryString,
  },
  opts = {},
) {
  const client = useApolloClient();

  const formatSort = useMemo(() => {
    if (!sort || sort === 'ALL') {
      return undefined;
    }

    /* istanbul ignore file -- @preserve */
    const DIRECTION_MAPPING = {
      ascending: 'ASC',
      descending: 'DESC',
      unsorted: undefined,
    };

    /* istanbul ignore file -- @preserve */
    return {
      column: sort,
      direction: DIRECTION_MAPPING[sortDirection] || 'ASC',
    };
  }, [sort, sortDirection]);

  const onCompleted = useCallback(() => {
    if (!updateAssetsQueryString) return;

    const cached = client.readQuery({ query: userInfoQuery });
    if (!cached) return;

    client.writeQuery({
      query: userInfoQuery,
      data: {
        currentUserProfile: {
          ...cached.currentUserProfile,
          user: {
            ...cached.currentUserProfile.user,
            assetsQueryString,
          },
        },
      },
    });
  }, [client, updateAssetsQueryString, assetsQueryString]);

  const {
    data: {
      assets: {
        collection: alertsData = [],
        pagination = {
          page: 1,
          perPage: 1,
          totalEntries: 0,
          totalPages: 1,
        },
        exportUrl,
      } = {},
    } = {},
    ...rest
  } = useQuery(assetAlertsQuery, {
    fetchPolicy: 'cache-and-network',
    variables: {
      assignees,
      alerts: alertTypes,
      alertsText,
      sort: formatSort,
      groupBy: groupBy === 'ALL' ? undefined : groupBy,
      assetTypes,
      makes,
      models,
      fleetInfo,
      years,
      costCenters,
      customAlerts,
      platformAccountIds,
      groupedAssetGroupingsIds,
      wip: workInProgress,
      customerIds,
      enabled,
      currentPage,
      platformCaseFleetStatus,
      platformCaseRepairStatus,
      serviceEventStatus,
      serviceRequestStatus,
      text,
      scheduleMaintenanceStatus,
      faultStatus,
      customAlertStatus,
      serviceEventCreatedBetween: buildDateRange(serviceEventCreatedBetween),
      serviceEventUpdatedBetween: buildDateRange(serviceEventUpdatedBetween),
      serviceRequestCreatedBetween: buildDateRange(
        serviceRequestCreatedBetween,
      ),
      serviceRequestUpdatedBetween: buildDateRange(
        serviceRequestUpdatedBetween,
      ),
      platformCaseCreatedBetween: buildDateRange(platformCaseCreatedBetween),
      platformCaseUpdatedBetween: buildDateRange(platformCaseUpdatedBetween),
      serviceProviderUuids,
      serviceProviderCityIds,
      includeClosedPlatformCases,
      assetsQueryString,
      updateAssetsQueryString,
    },
    onCompleted,
    ...opts,
  });

  return {
    alertsData,
    pagination,
    exportUrl,
    queryResult: rest,
  };
}
